import React, { Suspense, useEffect, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useGLTF, Preload } from "@react-three/drei";
import * as THREE from "three";

/*
 * One persistent background scene for the whole page.
 * The model glides between keyframes as the user scrolls —
 * hero → about → projects → contact — instead of separate
 * canvases restarting per section.
 *
 * The canvas runs frameloop="demand": it only renders while the
 * camera is actually moving (scroll / parallax), then goes idle
 * so a static page costs zero GPU time.
 */

const KEYFRAMES = [
  { pos: [0, -50, -150], rot: [50, 100, 0] }, // hero: classic lookout view
  { pos: [50, -55, -145], rot: [50, 100.12, 0] }, // about: drift right, slight turn
  { pos: [-15, -95, -200], rot: [49.94, 99.9, 0] }, // projects: pull back and down
  { pos: [10, -230, -310], rot: [50.08, 100.25, 0] }, // contact: recede into the night
];

const smoothstep = (t) => t * t * (3 - 2 * t);

const FirewatchModel = () => {
  const group = useRef();
  const pointer = useRef({ x: 0, y: 0 });
  const reducedMotion = useRef(false);
  const { scene } = useGLTF("/firewatch_g/scene.gltf");
  const invalidate = useThree((state) => state.invalidate);

  useEffect(() => {
    reducedMotion.current = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const wake = () => invalidate();

    // Canvas is pointer-events-none, so track the pointer on window
    const onMove = (e) => {
      pointer.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      pointer.current.y = (e.clientY / window.innerHeight) * 2 - 1;
      if (!reducedMotion.current) invalidate();
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("scroll", wake, { passive: true });
    window.addEventListener("resize", wake);
    invalidate();
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("scroll", wake);
      window.removeEventListener("resize", wake);
    };
  }, [invalidate]);

  useFrame((_, rawDelta) => {
    const g = group.current;
    if (!g) return;

    // Demand mode can pause between frames; clamp so damping never jumps
    const delta = Math.min(rawDelta, 0.1);

    const max = document.documentElement.scrollHeight - window.innerHeight;
    const p = max > 0 ? THREE.MathUtils.clamp(window.scrollY / max, 0, 1) : 0;
    const f = p * (KEYFRAMES.length - 1);
    const i = Math.min(Math.floor(f), KEYFRAMES.length - 2);
    const t = smoothstep(f - i);
    const a = KEYFRAMES[i];
    const b = KEYFRAMES[i + 1];

    const lambda = 2.2; // damping — higher is snappier
    let remaining = 0;
    for (let axis = 0; axis < 3; axis++) {
      const targetPos = a.pos[axis] + (b.pos[axis] - a.pos[axis]) * t;
      let targetRot = a.rot[axis] + (b.rot[axis] - a.rot[axis]) * t;

      if (!reducedMotion.current) {
        // Subtle mouse parallax on top of the scroll path
        if (axis === 1) targetRot += pointer.current.x * 0.015;
        if (axis === 0) targetRot += pointer.current.y * 0.008;
      }

      const pos = g.position.getComponent(axis);
      g.position.setComponent(
        axis,
        THREE.MathUtils.damp(pos, targetPos, lambda, delta)
      );

      const rotAxis = "xyz"[axis];
      g.rotation[rotAxis] = THREE.MathUtils.damp(
        g.rotation[rotAxis],
        targetRot,
        lambda,
        delta
      );

      remaining += Math.abs(targetPos - pos) + Math.abs(targetRot - g.rotation[rotAxis]) * 100;
    }

    // Keep rendering until the glide settles, then let the canvas sleep
    if (remaining > 0.05) invalidate();
  });

  return (
    <group
      ref={group}
      position={KEYFRAMES[0].pos}
      rotation={KEYFRAMES[0].rot}
    >
      <primitive object={scene} />
    </group>
  );
};

useGLTF.preload("/firewatch_g/scene.gltf");

const FirewatchScene = () => (
  <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true">
    <Canvas
      frameloop="demand"
      dpr={[1, 1.5]}
      gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
    >
      <ambientLight intensity={0.5} />
      <directionalLight position={[100, 10, 0]} intensity={50.8} />
      <Suspense fallback={null}>
        <FirewatchModel />
        <Preload all />
      </Suspense>
    </Canvas>
  </div>
);

export default FirewatchScene;
